import { useMemo, useState } from "react";
import {
  AddRemarks,
  LoanRemark,
  useAddLoanRemarkMutation,
  useGetLoanRemarksQuery,
} from "../../../actions/loans.actions";
import Button from "../../../components/button";
import ConfirmDialog, {
  ConfirmDialogType,
} from "../../../components/confirm-dialog";
import Modal from "../../../components/modal";
import Table, { TableColumns } from "../../../components/table";
import { dateToDDMMYYYY } from "../../../utils";
import { validateAddRemarksFields } from "../../loan-details/helpers";

const getTableDataForLoanRemarks = (loanRemarks: LoanRemark[]) => {
  return loanRemarks.map((_remark) => {
    const { remark, remarkDate } = _remark;
    return {
      remark,
      remarkDate: dateToDDMMYYYY(remarkDate),
    };
  });
};

const COLUMNS_FOR_LOAN_REMARKS: TableColumns[] = [
  {
    field: "remarkDate",
    label: "Remark Date",
    style: { textAlign: "center" },
  },
  {
    field: "remark",
    label: "Remark",
    style: { textAlign: "center" },
  },
];

interface LoanRemarksProps {
  loanId: string;
  loanType: "TERM_LOAN" | "RETAIL_OD" | "RETAIL_OD_DRAWDOWN";
}

export default function LoanRemarks({ loanId, loanType }: LoanRemarksProps) {
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const {
    data: loanRemarks,
    error: loanRemarksError,
    isLoading,
  } = useGetLoanRemarksQuery({
    entityId: loanId,
    loanType: loanType as "TERM_LOAN" | "RETAIL_OD" | "RETAIL_OD_DRAWDOWN",
  });
  const [triggerAddLoanRemark, { isLoading: isAddingRemark }] =
    useAddLoanRemarkMutation();

  async function handleSubmit(type: ConfirmDialogType, fieldData: any) {
    validateAddRemarksFields(fieldData);

    const payload: AddRemarks = {
      remark: fieldData.remark,
      remarkDate: fieldData.remarkDate,
    };

    const response = await triggerAddLoanRemark({
      entityId: loanId,
      loanType: loanType as "TERM_LOAN" | "RETAIL_OD" | "RETAIL_OD_DRAWDOWN",
      ...payload,
    })
      .unwrap()
      .catch(() => {
        setIsErrorModalOpen(true);
      });

    if (response?._id) {
      setTimeout(() => {
        setIsRemarkModalOpen(false);
      }, 3000);
    }

    return {
      message: `Remark added successfully for LoanId: ${response?._id}!`,
    };
  }

  const tableDataForLoanRemarks = useMemo(() => {
    if (!loanRemarks?.length) return [];
    return getTableDataForLoanRemarks(loanRemarks);
  }, [loanRemarks]);

  return (
    <>
      <div className="d-flex justify-content-between pb-3">
        <h3 className="user-page-title mb-4">Loan Remark</h3>
        <Button onClick={() => setIsRemarkModalOpen(true)}>Add Remark</Button>
      </div>
      <Table
        loading={isLoading || isAddingRemark}
        columns={COLUMNS_FOR_LOAN_REMARKS}
        data={tableDataForLoanRemarks}
      />
      <ConfirmDialog
        active={isRemarkModalOpen}
        title="Add Remarks"
        message="Please fill the below details to add remarks"
        onSubmit={handleSubmit}
        onCancel={() => setIsRemarkModalOpen(false)}
        submitButtonText="Add"
        cancelButtonText="Cancel"
        inputFields={[
          {
            name: "remark",
            label: "Remark",
            type: "text",
            placeholder: "remark",
            required: true,
          },
          {
            name: "remarkDate",
            label: "Remark date",
            type: "date",
            placeholder: "remark date",
            required: true,
          },
        ]}
      />
      <Modal
        open={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        children={
          true && (
            <div>
              <h5 className="text-danger">Error</h5>
              <p>
                {loanRemarksError
                  ? "Failed to fetch loan remarks. Please try again."
                  : "Failed to add remark. Please try again."}
              </p>
            </div>
          )
        }
        hasSubmitButton={false}
      />
    </>
  );
}
